import React, { memo, useMemo, useCallback, useEffect, useRef, useState, FC } from 'react';
import { ProductSize, ProductViewModel } from '~/lib/data-contract';
import { useCookieConsent } from '~/shared/hooks';
import { isBrowser, useFranchise } from '~/shared/utils';
import { EasysizeElementIds, EasysizeIdTable } from './settings';
import { runtimeConfig } from '~/shared/utils/public-variables';
import Script from 'next/script';
import { Easysize as EasysizeClass } from '~/global';
import { useSession } from 'next-auth/react';
import styles from './easy-size.module.css';
import clsx from 'clsx';

type Config = {
    product_id?: string;
    product_type?: string;
    product_brand?: string;
    product_gender?: string;
    image_url?: string;
    sizes_in_stock?: { [key: string]: string | null };
    user_id?: string;
    cookies_enabled?: boolean;
    shop_id?: string;
    language?: string;
    placeholder?: string;
    recommendation_placeholder?: string;
    add_to_cart_btn_selector?: string;
    size_selector?: string;
    loaded?: ({ is_product_supported }: { is_product_supported?: boolean }) => void;
};

const { ENVIRONMENT_NAME } = runtimeConfig();


type Props = {
    product: ProductViewModel;
    activeSizeId?: string | null;
    onSizeFound: (variantId: string) => void;
    disableAddToBasket?: boolean;
};

const EasySize: FC<Props> = memo((props) => {
    const { product, activeSizeId, onSizeFound, disableAddToBasket } = props;

    const session = useSession();
    const customerNumber = session.data?.user.customerNumber;
    const EasySizeRef = useRef<{ id?: string; instance?: EasysizeClass }>();
    const { productId, productType, brand, sizes = [], images } = product;
    const { key, activeMarket } = useFranchise();
    const { functional } = useCookieConsent();
    const {
        placeholder,
        recommendation_placeholder,
        add_to_cart_btn_selector,
    } = EasysizeElementIds;
    const [initFailed, setInitFailed] = useState(false);

    const setSuggestedVariant = useCallback(
        (suggestedSize?: ProductSize['size']) => {
            // Must return true or false, to let Easysize know if size was updated.
            // It's not enough to call sizeChangedTo('m') on actual size change.
            if (!suggestedSize) {
                return false;
            }
            const variant = sizes?.find((size) => size.size === suggestedSize);
            if (!(variant?.variantId && variant.inStockOnWeb)) {
                return false;
            }
            onSizeFound(variant.variantId);
            return true;
        },
        [sizes, onSizeFound]
    );

    // Map sizes so the key is the size, and the value is the stock.
    // In our case we don't know the stock value, so we send 0 or 1.
    // {size: '3XL', variantId: '5714392465778', inStockOnWeb: true} → { '3XL': 1 }
    const availableSizes = useMemo(
        () =>
            Object.assign(
                {},
                ...(sizes.map((size) => {
                    if (size.size) {
                        return { [size.size]: size.inStockOnWeb ? '1' : '0' };
                    }
                    return {};
                }) || {})
            ),
        [sizes]
    );

    const config: Config = useMemo(
        () => ({
            product_id: productId,
            product_type: productType,
            product_brand: brand,
            image_url: images?.[0]?.url,
            sizes_in_stock: availableSizes,
            user_id: customerNumber || '-1',
            shop_id: EasysizeIdTable[key]?.[ENVIRONMENT_NAME],
            language: activeMarket?.languageName?.toLocaleLowerCase(),
            cookies_enabled: Boolean(functional),
            placeholder: `#${placeholder}`,
            recommendation_placeholder: `#${recommendation_placeholder}`,
            ...((!disableAddToBasket) && { add_to_cart_btn_selector: `#${add_to_cart_btn_selector}` }),
            product_gender: 'm',
            loaded: ({ is_product_supported }) => {
                if (!is_product_supported) {
                    setInitFailed(true);
                }
            },
            size_update_function: setSuggestedVariant,
        }),
        [
            productId,
            productType,
            brand,
            images,
            availableSizes,
            customerNumber,
            key,
            activeMarket?.languageName,
            functional,
            placeholder,
            recommendation_placeholder,
            add_to_cart_btn_selector,
            setSuggestedVariant,
            disableAddToBasket,
        ]
    );

    const startEasysize = useCallback(() => {
        const colorId = product.productColorId;
        // If the colorId is equal to the ref, nothing has changed
        // and we do not wish to call .start() on the Easysize instance
        const hasIdChanged = colorId !== EasySizeRef.current?.id;
        const isConsentLoaded = functional !== undefined;
        if (!window.EasySize || !isConsentLoaded || !hasIdChanged) {
            return;
        }
        if (!EasySizeRef.current) {
            EasySizeRef.current = {};
        }
        EasySizeRef.current.id = colorId;
        delete EasySizeRef.current.instance;
        EasySizeRef.current.instance = new window.EasySize(config);
        EasySizeRef.current.instance.start();
    }, [config, EasySizeRef, product.productColorId, functional]);

    useEffect(() => {
        startEasysize();
    }, [startEasysize]);

    useEffect(() => {
        if (EasySizeRef.current?.instance && activeSizeId) {
            // Easysize needs to know if size has changed
            const size = sizes.find((size) => size.variantId === activeSizeId)?.size;
            if (!size) {
                return;
            }
            EasySizeRef.current.instance.sizeChangedTo(size);
        }
    }, [sizes, activeSizeId]);

    const onEasysizeScriptError = useCallback(() => {
        console.warn('Easysize could not be loaded');
        setInitFailed(true);
    }, [setInitFailed]);

    return (
        <>
            {!initFailed && (
                <div className={clsx(styles.wrapper, 'hover-effect-underline-center')}>
                    <span id={placeholder} />
                </div>
            )}
            {isBrowser && (
                <Script
                    onLoad={startEasysize}
                    onError={onEasysizeScriptError}
                    async={true}
                    charSet="utf-8"
                    src='https://webapp.easysize.me/web_app_v1.0/js/easysize.js'
                />
            )}
        </>
    );
});

EasySize.displayName = 'EasySize';
export default EasySize;
